import { useState, useRef, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useWarningsData } from 'Modules/devices/.hooks/useWarningsData';
import { NavLink } from "react-router-dom";
import { useIntl } from "react-intl";

//Components
import { Grid, Typography, Container, Paper, Divider, Icon, IconButton, Chip } from "@mui/material";
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import { Button, Select, Tooltip } from "@gpstrackit/react-controls";
import ChipStatus from 'Components/ChipStatus';
import InfoIcon from '@mui/icons-material/Info';
import ProgressBar from "Components/ProgressBar";
import { TagsGroup } from 'Components/Tag';

//EndPoints
import { endpoints } from 'Core/defaultValues';

//Views
import AdvancedFilters from 'Modules/devices/views/main/.components/AdvancedFilters';
import Details from 'Modules/devices/views/main/.components/Details';
import BulkPairing from 'Modules/devices/.components/bulkPairing';
import VidfleetConfiguration from "Modules/vidfleet/.components/configuration";
import ConfigurationUploadMode from "Modules/vidfleet/.components/configurationUploadMode";
import ConfigurationHubSpot from "Modules/vidfleet/.components/configurationHubSpot";
import BulkActions from "Modules/devices/.components/bulkActions";
import ActivateSimCard from 'Modules/devices/.components/activateSimCard';
import ChangeSimProvider from 'Modules/devices/.components/changeSimProvider';
import ImportDevices from 'Modules/devices/.components/importDevices';
import BulkUnpairing from 'Modules/devices/.components/bulkUnpairing';
import AssignFirmware from 'Modules/devices/.components/assignFirmware';
import VidfleetInformation from 'Modules/.common/.components/vidfleetInformation';
import EditSwapSimCard from 'Modules/devices/.components/editSwapSimCard';
import ChangePulsCustomer from 'Modules/devices/.components/changePulsCustomer';
import UpdateFeature from 'Modules/devices/.components/featureTab/.components/updateFeature';
import ChangeScript from 'Modules/devices/.components/changeScript';
import SendCommand from 'Modules/devices/.components/sendCommand';
import MarkAsRma from 'Modules/devices/.components/markAsRma';
import ChangeStatus from 'Modules/devices/.components/changeStatus';
import ChangeTimezone from 'Modules/devices/.components/changeTimezone';
import RebootDevice from 'Modules/devices/.components/rebootDevice';
import CreateDiagnosticDevice from 'Modules/devices/.components/createDiagnosticDevice';
import CoverageDetails from 'Modules/devices/.components/coverageDetails'
import MaintenanceWarnings from 'Modules/devices/.components/maintenanceWarnings';
import VidFleetOverview from "Modules/devices/.components/vidFleetVideo";
import BulkTags from 'Modules/tags/.components/bulkTags';
import Activities from "Modules/activities/.components/tab";
import AssignBillingAccount from "Modules/dealers/views/.components/assignBillingAccount";
import CastImportManual from 'Modules/devices/.components/castImportManual';


//Notification
import { NotificationManager } from "Components/Notifications";

//Helpers
import { getDateFormatted, getBadget, getDevicesTypeSearch, getWarningCategory, getTagColor } from 'Core/data/Helpers';

//Actions
import {
    bulkPairingOpenModal,
    vidfleetConfigurationBulkOpenModal,
    vidfleetConfigurationHubSpotLoadData,
    bulkActionsOpenModal,
    activateCameraSimCardOpenModal,
    changeSimProviderOpenModal,
    changePulsCustomerOpenModal,
    vidfleetConfigurationUploadModeLoadData,
    devicesImportShow,
    bulkUnpairingOpenModal,
    assignFirmwareOpenModal,
    vidfleetInformationOpenModal,
    editSwapSimCardOpenModal,
    deviceDetailShow,
    featureTabOpenModal,
    changeScriptOpenModal,
    sendCommandOpenModal,
    searchEntityRequest,
    markAsRmaOpenModal,
    clearRedux,
    changeStatusOpenModal,
    changeTimezoneOpenModal,
    rebootDeviceOpenModal,
    createDiagnosticDeviceOpenModal,
    coverageDetailsOpenModal,
    openModalMaintenanceWarnings,
    openModalCastImportManual,
    vidFleetVideoOpenModal,
    bulkTagsOpenModal,
    activitiesSearchOpenModal,
    openModalAssignBillingAccount,
} from 'Redux/actions';

// RTK Slice actions/thunks - COMMENTED OUT: Now using commonISRedux via entity prop
// import { fetchDevicesMain, fetchDevicesMainTotalCount, clearDevicesMain } from 'Modules/devices/views/main/redux/slice';

//Permission
import { HasPermission } from "Core/security";

// Stable selector — defined outside the component so useSelector only re-renders on items changes,
// not on every entity state update (loading, totalCount, etc.)
const EMPTY_ITEMS = [];
const selectDevicesItems = state =>
    state.commonISRedux?.entities?.['devices']?.items ?? EMPTY_ITEMS;

const DevicesMain = (props) => {
    const { messages } = useIntl();
    const tableRef = useRef(null);
    // COMMENTED OUT: fetchPromiseRef no longer needed - commonISRedux handles abort internally
    // const fetchPromiseRef = useRef(null);
    const dispatch = useDispatch();

    const statusOptions = [
        {
            id: 1,
            label: messages['active']
        },
        {
            id: 4,
            label: messages['cancelled']
        },
        {
            id: 3,
            label: messages['involuntary_suspension']
        },
        {
            id: 2,
            label: messages['voluntary_suspension']
        }
    ]

    //Redux States
    const configurationUploadModeOpenModal = useSelector(state => state.vidfleetConfigurationUploadModeRedux.openModal);
    const openModalVidFleetConfiguration = useSelector(state => state.vidfleetConfigurationRedux.openModal);
    const configurationHubSpotOpenModal = useSelector(state => state.vidfleetConfigurationHubSpotRedux.openModal)
    const changeSimPOpenModal = useSelector(state => state.changeSimProviderRedux.openModal);
    const activeSimCardOpenModal = useSelector(state => state.activateCameraSimCardRedux.openModal);
    const bulkPOpenModal = useSelector(state => state.bulkPairingRedux.openModal);
    const bulkAOpenModal = useSelector(state => state.bulkActionsRedux.openModal);
    const importDevicesOpenModal = useSelector(state => state.importDevicesRedux.openModal);
    const bulkUnOpenModal = useSelector(state => state.bulkUnpairingRedux.openModal);
    const assignFirmOpenModal = useSelector(state => state.assignFirmwareDeviceRedux.openModal);
    const deviceInfoOpenModal = useSelector(state => state.deviceInfoRedux.openModal);
    const editSwapSimCardOModal = useSelector(state => state.editSwapSimCardRedux.openModal);
    const deviceDetailsOpenModal = useSelector(state => state.deviceDetailsRedux.openModal);
    const cgPulsCustomerOpenModal = useSelector(state => state.changePulsCustomerRedux.openModal);
    const editDeviceOpenModal = useSelector(state => state.featureTabUpdateFeatureRedux.openModal);
    const cgScriptOpenModal = useSelector(state => state.changeScriptRedux.openModal);
    const sendCommandsOpenModal = useSelector(state => state.sendCommandRedux.openModal);
    const firmwares = useSelector(state => state.generalRedux.firmwares);
    const { refreshResult, loading } = useSelector(state => state.deviceOverviewRedux);
    const openModalMarkAsRma = useSelector(state => state.markAsRmaRedux.openModal);
    const changeStatusOModal = useSelector(state => state.changeStatusRedux.openModal);
    const changeTimezoneOModal = useSelector(state => state.changeTimezoneRedux.openModal);
    const events = useSelector(state => state.generalRedux.events);
    const rebootDeOpenModal = useSelector(state => state.rebootDeviceRedux.openModal);
    const openModalCoverageDetails = useSelector(state => state.coverageDetailsRedux.openModal);
    const openModalCreateDiagnosticDevice = useSelector(state => state.createDiagnosticDeviceRedux.openModal);
    const user = useSelector(state => state.securityRedux.user);
    const openModalViewMaintenanceWarnings = useSelector(state => state.maintenanceWarningsRedux.openModal);
    const openModalVidFleetVideo = useSelector(state => state.vidFleetVideoRedux.openModal);
    const openModalBulkTags = useSelector(state => state.bulkTagsRedux.openModal);
    const openModalActivities = useSelector(state => state.activitiesTabRedux.openModal);
    const assignBillingAccountOpenModal = useSelector(state => state.assignBillingAccountRedux.openModal);
    const castImportManualOpenModal = useSelector(state => state.castImportManualRedux.openModal);
    const devicesItems = useSelector(selectDevicesItems);

    const { warningsMap, fetchWarningsForIds, resetWarnings } = useWarningsData();

    useEffect(() => {
        const ids = devicesItems.map(d => d.id).filter(Boolean);
        if (ids.length > 0) fetchWarningsForIds(ids);
    }, [devicesItems, fetchWarningsForIds]);

    // COMMENTED OUT: Devices Main Redux State - Now using commonISRedux via entity="devices"
    // The DataViewInfiniteScroll component will automatically manage state via commonISRedux
    // when the 'entity' prop is provided without 'externalDataConfig'
    // const { 
    //     loading: devicesMainLoading, 
    //     devices: devicesMainData, 
    //     total: devicesMainTotal,
    //     nextToken: devicesMainNextToken,
    //     totalCount: devicesMainTotalCount,
    //     totalCountLoading: devicesMainTotalCountLoading,
    // } = useSelector(state => state.devicesMainRedux);

    //Local States
    const [columns, setColumns] = useState([
        {
            key: 'view',
            show: true,
            locked: true,
            title: "",
            width: 40,
            icon: true,
            render: (item) => {
                return (
                    <Icon style={{ cursor: "pointer", color: '#1D2734' }} className="toolbar-list-icon fs-20" onClick={() => {
                        dispatch(deviceDetailShow(item.id))
                    }}>
                        remove_red_eye
                    </Icon>
                )
            }
        },
        {
            key: 'laveStraming',
            show: true,
            locked: true,
            title: "",
            width: 40,
            icon: true,
            position: 1,
            render: (item) => {
                if (item?.deviceModel?.manufacturer?.name?.toUpperCase() === "Vidfleet".toUpperCase())
                    return (
                        <Tooltip title={messages['devices_main_live_streaming']}>
                            <Icon style={{ cursor: "pointer", color: '#1D2734' }} className="toolbar-list-icon fs-20" onClick={() => {
                                dispatch(vidFleetVideoOpenModal({
                                    messages: messages, //For to access to language keys
                                    mode: "video",
                                    type: "streaming", //Depends of the playing type, the url VidFleet API changes
                                    id: item?.esn, //This could be a ESN or Clip Id
                                    autoplay: true, //If is true the video will be played automatically
                                    deviceModel: item?.deviceModel?.name?.includes("360") ? '360' : '4k', //4k|360
                                    disabled: false,
                                    timezone: item?.timezone
                                }));
                            }}>
                                video_call
                            </Icon>
                        </Tooltip>
                    )
            }
        },
        {
            key: 'activities',
            show: true,
            locked: true,
            title: "",
            width: 40,
            icon: true,
            position: 2,
            render: (item) => {
                return (
                    <Tooltip title={messages['users_tab_show_activities']}>
                        <Icon style={{ cursor: "pointer" }} className="toolbar-list-icon fs-20" onClick={() => {
                            if (HasPermission(user, "act")) {
                                dispatch(activitiesSearchOpenModal({ entityName: "Unit", entityId: item.id }, "devices-main-modal"))
                            } else {
                                NotificationManager.warning(
                                    messages['common_error_permission'],
                                    messages['users_tab_users'],
                                    5000,
                                );
                            }
                        }} >
                            assignment
                        </Icon>
                    </Tooltip>
                )
            }
        },
        {
            key: "id",
            field: "id",
            title: messages['devices_main_id'],
            show: true,
            resize: true,
            width: 150,
            export: true,
            sort: true,
            functionString: 'return row?.id',
            code: "devices_main_id",
            render: (item) => {
                return <span>{item?.id}</span>
            }
        },
        {
            key: "label",
            field: "label",
            title: messages['devices_main_label'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            code: "devices_main_label",
            functionString: 'return row?.label',
            render: (item) => {
                return (
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        className="context-menu-container"
                        wrap="nowrap"
                    >
                        <Grid item>
                            <NavLink to={`/app/devices/overview/${item?.id}`} style={{ color: 'black' }}>
                                <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.label}</span>
                            </NavLink>
                        </Grid>
                    </Grid>
                )
            }
        },
        {
            key: "company_name",
            field: "company { id name }",
            title: messages['devices_main_company'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.company?.name',
            code: "devices_main_company",
            render: (item) => {
                return (
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        className="context-menu-container"
                        wrap="nowrap"
                    >
                        <Grid item>
                            <NavLink to={`/app/accounts/overview/${item?.company?.id}`} style={{ color: 'black' }}>
                                <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.company?.name}</span>
                            </NavLink>
                        </Grid>
                    </Grid>
                )
            }
        },
        {
            key: "company_crmId",
            field: "company { crmId }",
            title: messages['accounts_tab_crm'],
            width: 250,
            show: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.company?.crmId',
            code: "accounts_tab_crm",
            render: (item) => {
                return <a
                    style={{ color: 'black' }}
                    href={`${endpoints.SALESFORCE_URL}lightning/r/Account/${item?.company?.crmId}/view`}
                    target="_blank"
                >
                    {item?.company?.crmId}
                </a>
            }
        },
        {
            key: "company_dealer_name",
            field: "company { dealer { id name brandId } }",
            title: messages['devices_main_brand'],
            width: 250,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.company?.dealer?.name',
            code: "devices_main_brand",
            render: (item) => {
                return <span>{item?.company?.dealer?.name}</span>
            }
        },
        {
            key: "deviceModel_name",
            title: messages['devices_main_device_model'],
            width: 350,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.deviceModel?.name',
            code: "devices_main_device_model",
            render: (item) => {
                return <span>{item?.deviceModel?.name}</span>
            }
        },
        {
            key: "deviceModel_unitType_name",
            field: "deviceModel { unitType { name } }",
            title: messages['devices_main_unit_type'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.deviceModel?.unitType?.name',
            code: "devices_main_unit_type",
            render: (item) => {
                return <span>{item?.deviceModel?.unitType?.name}</span>
            }
        },
        {
            key: "simCard_iccId",
            field: "simCard.iccId",
            title: messages['devices_main_sim_number'],
            width: 200,
            show: false,
            locked: false,
            resize: true,
            export: true,
            sort: true,
            functionString: 'return row?.simCard?.iccId',
            code: "devices_main_sim_number",
            render: (item) => {
                return <span>{item?.simCard?.iccId}</span>
            }
        },
        {
            key: "deviceModel_manufacturer_name",
            title: messages['devices_main_manufacturer'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.deviceModel?.manufacturer?.name',
            code: "devices_main_manufacturer",
            render: (item) => {
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: 5 }}>{item?.deviceModel?.manufacturer?.name}</span>
                        {
                            item?.deviceModel?.manufacturer?.name?.toUpperCase() === "Vidfleet".toUpperCase() &&
                            <Tooltip
                                title={messages['vidfleet_show_vidfleet_info']}
                            >
                                <IconButton
                                    style={{ padding: 0, color: '#444444' }}
                                    onClick={() => dispatch(vidfleetInformationOpenModal(item.esn, item.id, item?.timezone, item?.companyId))}
                                    size="large">
                                    <InfoIcon />
                                </IconButton>
                            </Tooltip>
                        }
                    </div>
                );
            }
        },
        {
            key: "imei",
            field: "imei",
            title: "IMEI",
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.imei',
            render: (item) => {
                return (
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        className="context-menu-container"
                        wrap="nowrap"
                    >
                        <Grid item>
                            <NavLink to={`/app/devices/overview/${item?.id}`} style={{ color: 'black' }}>
                                <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.imei}</span>
                            </NavLink>
                        </Grid>
                    </Grid>
                )
            }
        },
        {
            key: "esn",
            title: messages['devices_main_serial'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.esn',
            code: "devices_main_serial",
            render: (item) => {
                return (
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        className="context-menu-container"
                        wrap="nowrap"
                    >
                        <Grid item>
                            <NavLink to={`/app/devices/overview/${item?.id}`} style={{ color: 'black' }}>
                                <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.esn}</span>
                            </NavLink>
                        </Grid>
                    </Grid>
                )
            }
        },
        {
            key: "simCard_msisdn",
            field: "simCard.msisdn",
            title: messages['devices_tab_sim_phone'],
            width: 200,
            show: false,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.simCard?.msisdn',
            code: "devices_tab_sim_phone",
            render: (item) => {
                return <span>{item?.simCard?.msisdn}</span>
            }
        },
        {
            key: "status",
            field: "status",
            title: messages['devices_main_status'],
            sort: true,
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return `${row?.status === 0 ? "Desactivated" : row?.status === 1 ? "Active": row?.status === 2 ? "Voluntary Suspension" : row?.status === 3 ? "Involuntary Suspension" : row?.status === 4 ? "Cancelled" : row?.status === 6 ? "RMA" : row?.status === 20 ? "Inactive" : "Unknown" }`',
            code: "devices_main_status",
            render: (item) => {
                let nextScheduledAction = item?.nextScheduledAction
                let message = null
                if (nextScheduledAction) {
                    try {
                        let data = JSON.parse(nextScheduledAction)
                        if (data && data?.action === "statusUpdate") {
                            message = `${messages['accounts_main_next_scheduled_action']}: ${data?.scheduleOn}`
                            let status = statusOptions.find(item => item?.id === data?.data?.newStatusId)
                            message = `${messages[data?.action] || data?.action}${status ? ` ${messages[status?.label] || status?.label}` : ''} ${messages['accounts_main_at']} ${data?.scheduleOn}`
                        }
                    } catch (exc) { }
                }
                return (
                    <div style={{ display: 'flex' }}>
                        <ChipStatus statusCode={item?.status} messages={messages} />
                        {nextScheduledAction &&
                            <div style={{ marginLeft: 5 }}>
                                <Tooltip title={message}>
                                    <Icon style={{ color: '#1664f5', cursor: 'pointer' }}>access_alarms</Icon>
                                </Tooltip>
                            </div>
                        }
                    </div>
                )
            }
        },
        {
            key: "network",
            field: "network",
            title: messages['devices_main_network'],
            sort: true,
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.network',
            code: "devices_main_network",
            render: (item) => {
                return <span>{item?.network || ""}</span>
            }
        },
        {
            key: "reference",
            field: "reference",
            title: messages['devices_main_reference'],
            show: true,
            resize: true,
            width: 250,
            export: true,
            sort: true,
            functionString: 'return row?.reference',
            code: "devices_main_reference",
            render: (item) => {
                return <span>{item?.reference}</span>
            }
        },
        {
            key: "simCard_simProviderAccount_name",
            field: "simCard.simProviderAccount { name simProvider { name } }",
            title: messages['devices_main_sim_provider_account'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.simCard?.simProviderAccount?.name',
            code: "devices_main_sim_provider_account",
            render: (item) => {
                return <span>
                    {item?.simCard?.simProviderAccount?.name || ""}
                </span>
            }
        },
        {
            key: "simCard_simProviderAccount_simProvider_name",
            field: "",
            title: messages['devices_main_sim_provider'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.simCard?.simProviderAccount?.simProvider?.name',
            code: "devices_main_sim_provider",
            render: (item) => {
                return <span>
                    {item?.simCard?.simProviderAccount?.simProvider?.name || ""}
                </span>
            }
        },
        {
            key: "linkedUnitIdLabel",
            field: "linkedUnit { id label }",
            title: messages['devices_main_linked_device_label'],
            width: 200,
            show: false,
            locked: false,
            resize: true,
            export: true,
            // sort: true — REMOVED: backend returns "Variable 'field' has an invalid value"
            functionString: 'return row?.linkedUnit?.label',
            code: "devices_main_linked_device_label",
            render: (item) => {
                return (
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        className="context-menu-container"
                        wrap="nowrap"
                    >
                        <Grid item>
                            <NavLink to={`/app/devices/overview/${item?.linkedUnit?.id}`} style={{ color: 'black' }}>
                                <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.linkedUnit?.label}</span>
                            </NavLink>
                        </Grid>
                    </Grid>
                )
            }
        },
        {
            key: "linkedUnitId",
            field: "linkedUnit { id esn }",
            title: messages['devices_main_linked_device'],
            width: 200,
            show: false,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.linkedUnit?.esn',
            code: "devices_main_linked_device",
            render: (item) => {
                return (
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        className="context-menu-container"
                        wrap="nowrap"
                    >
                        <Grid item>
                            <NavLink to={`/app/devices/overview/${item?.linkedUnit?.id}`} style={{ color: 'black' }}>
                                <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.linkedUnit?.esn}</span>
                            </NavLink>
                        </Grid>
                    </Grid>
                )
            }
        },
        {
            key: "linkedUnitBy_label",
            field: "linkedByUnit { id esn }",
            title: messages['devices_main_linked_camera'],
            show: true,
            resize: true,
            width: 250,
            export: true,
            functionString: 'return row?.linkedByUnit?.esn',
            code: "devices_main_linked_camera",
            render: (item) => {
                return (
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        className="context-menu-container"
                        wrap="nowrap"
                    >
                        <Grid item>
                            <NavLink to={`/app/devices/overview/${item?.linkedByUnit?.id}`} style={{ color: 'black' }}>
                                <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.linkedByUnit?.esn}</span>
                            </NavLink>
                        </Grid>
                    </Grid>
                )
            }
        },
        {
            key: 'Group',
            field: "unitConfig.pulsGroup",
            show: true,
            locked: false,
            title: messages["devices_main_puls_group"],
            width: 200,
            resize: true,
            export: true,
            functionString: 'return row?.unitConfig?.pulsGroup',
            code: "devices_main_puls_group",
            render: (item) => {
                return <span>
                    {item?.unitConfig?.pulsGroup || ""}
                </span>
            }
        },
        {
            key: "createdOn",
            field: "createdOn",
            title: messages['devices_main_createdOn'],
            sort: true,
            width: 250,
            show: true,
            locked: false,
            resize: true,
            export: true,
            functionString: `
            function formatDate(date,tz) {
                let dateToformat = moment(date)
                let formatted = moment(dateToformat).tz(tz).format("YYYY/MM/DD HH:mm:ss")
                return formatted;
              }

              return row?.createdOn ? formatDate(row?.createdOn,row?.timezone) : ''
            `,
            code: "devices_main_createdOn",
            render: (item) => {
                return <span>{item?.createdOn && getDateFormatted(item?.createdOn, "date", "datetime", null, item?.timezone)}</span>
            }
        },
        {
            key: "updatedOn",
            field: "updatedOn",
            title: messages['devices_main_updatedOn'],
            sort: true,
            width: 250,
            show: true,
            locked: false,
            resize: true,
            export: true,
            functionString: `
            function formatDate(date,tz) {
                let dateToformat = moment(date)
                let formatted = moment(dateToformat).tz(tz).format("YYYY/MM/DD HH:mm:ss")
                return formatted;
              }

              return row?.updatedOn ? formatDate(row?.updatedOn,row?.timezone) : ''
            `,
            code: "devices_main_updatedOn",
            render: (item) => {
                return <span>{item?.updatedOn && getDateFormatted(item?.updatedOn, "date", "datetime", null, item?.timezone)}</span>
            }
        },
        {
            key: "lastReading_unitTime",
            field: "lastReading.unitTime",
            title: messages['devices_main_unitTime'],
            width: 250,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: `
            function formatDate(date,tz) {
                let dateToformat = moment(date)
                let formatted = moment(dateToformat).tz(tz).format("YYYY/MM/DD HH:mm:ss")
                return formatted;
              }
            return row?.lastReading?.unitTime ? formatDate(row?.lastReading?.unitTime,row?.timezone) : ''
        `,
            code: "devices_main_unitTime",
            render: (item) => {
                return <span>{item?.lastReading?.unitTime && getDateFormatted(item?.lastReading?.unitTime, "date", "datetime", null, item?.timezone)}</span>
            }
        },
        {
            key: "lastReading_serverTime",
            field: "lastReading.serverTime",
            title: messages['devices_main_serverTime'],
            width: 250,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: `
            function formatDate(date,tz) {
                let dateToformat = moment(date)
                let formatted = moment(dateToformat).tz(tz).format("YYYY/MM/DD HH:mm:ss")
                return formatted;
              }
            return row?.lastReading?.serverTime ? formatDate(row?.lastReading?.serverTime,row?.timezone) : ''
        `,
            code: "devices_main_serverTime",
            render: (item) => {
                return <span>{item?.lastReading?.serverTime && getDateFormatted(item?.lastReading?.serverTime, "date", "datetime", null, item?.timezone)}</span>
            }
        },
        {
            key: "lastReading_lastMessageTime",
            field: "lastReading.lastMessageTime",
            title: messages['devices_main_lastMessageTime'],
            width: 250,
            show: true,
            locked: false,
            sort: true,
            resize: true,
            export: true,
            functionString: `
            function formatDate(date,tz) {
                let dateToformat = moment(date)
                let formatted = moment(dateToformat).tz(tz).format("YYYY/MM/DD HH:mm:ss")
                return formatted;
              }
            return row?.lastReading?.lastMessageTime ? formatDate(row?.lastReading?.lastMessageTime,row?.timezone) : ''
        `,
            code: "devices_main_lastMessageTime",
            render: (item) => {
                return <span>{item?.lastReading?.lastMessageTime && getDateFormatted(item?.lastReading?.lastMessageTime, "date", "datetime", null, item?.timezone)}</span>
            }
        },
        {
            key: "age",
            title: messages['devices_main_age'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            functionString: `
            function fromNowOn(date) {
                let dateToformat = moment(date)
                return dateToformat.fromNow()
              }

              return row?.lastReading?.lastMessageTime ? fromNowOn(row?.lastReading?.lastMessageTime) : ''
            `,
            code: "devices_main_age",
            render: (item) => {
                return <span>{item?.lastReading?.lastMessageTime && getDateFormatted(item?.lastReading?.lastMessageTime, "date", "age", null, item?.timezone)}</span>
            }
        },
        {
            key: 'Firmware',
            show: false,
            locked: false,
            title: messages["devices_main_firmware"],
            width: 200,
            resize: true,
            export: true,
            functionString: 'return row?.unitConfig?.firmwareId',
            code: "devices_main_firmware",
            render: (item) => {
                let currentHardwareVersion = item?.unitConfig?.hardwareVersion
                let firmware = item?.unitConfig?.firmwareId;
                let defaultFirmware = firmwares?.find((x) => x.isDefault && x.hardwareVersion === currentHardwareVersion)
                let currentFirmware = firmwares?.find((x) => x.version === item?.unitConfig?.firmwareId)

                if (firmware && firmware !== "")
                    return (
                        <Grid>
                            <div style={{ display: 'flex' }}>
                                {
                                    _.parseInt(defaultFirmware?.firmwareId) > _.parseInt(currentFirmware?.firmwareId) ?
                                        <div className="d-flex">
                                            <Tooltip title={messages['assign_firmware_outdated']}>
                                                <Chip style={{
                                                    backgroundColor: "#FF0000",
                                                    color: "#ffffff"
                                                }} label={firmware || ''}></Chip>
                                            </Tooltip>
                                        </div>
                                        :
                                        <div className="d-flex">
                                            <Tooltip title={messages['assign_firmware_updated']}>
                                                <Chip style={{
                                                    backgroundColor: "#66BB6A",
                                                    color: "#ffffff"
                                                }} label={firmware || ''}></Chip>
                                            </Tooltip>
                                        </div>
                                }
                            </div>
                        </Grid>
                    )
            }
        },
        {
            key: "unitConfig_hardwareVersion",
            field: "unitConfig.hardwareVersion",
            title: messages['devices_main_hardware_version'],
            width: 200,
            show: false,
            locked: false,
            resize: true,
            export: true,
            sort: true,
            functionString: 'return row?.unitConfig?.hardwareVersion',
            code: "devices_main_hardware_version",
            render: (item) => {
                return <span>{item?.unitConfig?.hardwareVersion}</span>
            }
        },
        {
            key: "linkedMode",
            field: "linkedMode",
            title: messages['devices_main_mode'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            sort: true,
            export: true,
            functionString: 'return `${row?.deviceModel?.manufacturer?.name === "VidFleet"? row?.linkedMode ? "Paired Mode" : "Camera as GPS" : ""}`',
            code: "devices_main_mode",
            render: (item) => {
                return <span>
                    {
                        (item?.deviceModel?.manufacturer?.name?.toUpperCase() === "Vidfleet".toUpperCase() || item?.deviceModel?.manufacturer?.name?.toUpperCase() === "Smartwitness".toUpperCase() || item?.deviceModel?.manufacturer?.name?.toUpperCase() === "LightMetrics".toUpperCase()) && 
                        getBadget(item?.linkedMode ? messages["devices_import_camera_paired_mode"] : messages["devices_import_camera_gps"], item?.linkedMode)
                    }
                </span>
            }
        },
        {
            key: "show",
            field: "show",
            title: messages['devices_main_show_in_web'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            sort: true,
            code: "devices_main_show_in_web",
            functionString: 'return `${row?.show ? "Y" : "N" }`',
            render: (item) => {
                return <span>
                    {
                        item?.show ? messages['devices_bulk_yes'] : messages['devices_bulk_no']
                    }
                </span>
            }
        },
        {
            key: "installed",
            field: "installed",
            title: messages['devices_main_installed'],
            sort: true,
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return `${row?.installed ? "Installed" : "No Installed" }`',
            code: "devices_main_installed",
            render: (item) => {
                return <Chip style={{ backgroundColor: item?.installed ? "#66BB6A" : "#5A5A5A", color: "#FFFFFF" }} label={item?.installed ? messages['devices_main_installed'] : messages['devices_main_pending_installed'] || ''}></Chip>
            }
        },
        {
            key: "installedOn",
            field: "installedOn",
            title: messages['devices_main_installed_on'],
            width: 250,
            show: true,
            locked: false,
            resize: true,
            export: true,
            sort: true,
            functionString: `
            function formatDate(date,tz) {
                let dateToformat = moment(date)
                let formatted = moment(dateToformat).tz(tz).format("YYYY/MM/DD HH:mm:ss")
                return formatted;
              }
            return row?.installedOn ? formatDate(row?.installedOn,row?.timezone) : ''`,
            code: "devices_main_installed_on",
            render: (item) => {
                return <span>{item?.installedOn && getDateFormatted(item?.installedOn, "date", "datetime", null, item?.timezone)}</span>
            }
        },
        {
            key: "unitConfig_coverageScore",
            field: "unitConfig.coverageScore",
            title: messages['devices_main_coverage_score'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            sort: true,
            functionString: 'return row?.unitConfig?.coverageScore',
            code: "devices_main_coverage_score",
            render: (item) => {
                return <span style={{ cursor: "pointer", textDecoration: "underline" }} onClick={() => {
                    dispatch(coverageDetailsOpenModal(item.id))
                }}>
                    {
                        `${item?.unitConfig?.coverageScore != null ? `${item?.unitConfig?.coverageScore.toFixed(2)}%` : ""}`
                    }
                </span>
            }
        },
        {
            key: "unitConfig_coverageUpdatedOn",
            field: "unitConfig.coverageUpdatedOn",
            title: messages['devices_main_coverage_updated_on'],
            width: 250,
            show: true,
            locked: false,
            resize: true,
            export: true,
            functionString: `
            function formatDate(date,tz) {
                let dateToformat = moment(date)
                let formatted = moment(dateToformat).tz(tz).format("YYYY/MM/DD HH:mm:ss")
                return formatted;
              }
            return row?.unitConfig?.coverageUpdatedOn ? formatDate(row?.unitConfig?.coverageUpdatedOn,row?.timezone) : ''
        `,
            code: "devices_main_coverage_updated_on",
            render: (item) => {
                return <span>{item?.unitConfig?.coverageUpdatedOn && getDateFormatted(item?.unitConfig?.coverageUpdatedOn, "date", "datetime", null, item?.timezone)}</span>
            }
        },
        {
            key: "unitConfig_healthScore",
            field: "unitConfig.healthScore",
            title: messages['devices_main_maintenance_health_score'],
            width: 250,
            show: true,
            locked: false,
            resize: true,
            export: true,
            sort: true,
            functionString: 'return row?.unitConfig?.healthScore ? row?.unitConfig?.healthScore.toFixed(2) : 0',
            code: "devices_main_maintenance_health_score",
            render: (item) => {
                return <span>{item?.unitConfig?.healthScore ? item?.unitConfig?.healthScore?.toFixed(2) : 0}</span>
            }
        },
        {
            key: "liveMode",
            field: "liveMode",
            title: messages['devices_main_live_mode'],
            width: 250,
            show: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.liveMode',
            code: "devices_main_live_mode",
            render: (item) => {
                return <span>{item?.liveMode}</span>
            }
        },
        {
            key: "batteryLevel",
            field: "lastReading.deviceData",
            title: messages['devices_main_battery_level'],
            width: 250,
            show: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.lastReading?.deviceData ? JSON.parse(row?.lastReading?.deviceData).batteryPercent ? JSON.parse(row?.lastReading?.deviceData).batteryPercent: "" : ""',
            code: "devices_main_battery_level",
            render: (item) => {
                let battery = item?.lastReading?.deviceData ? JSON.parse(item.lastReading.deviceData).batteryPercent ? JSON.parse(item.lastReading.deviceData).batteryPercent : null : null
                let color = "green"
                if (battery && battery < 10) {
                    color = "red"
                } else if (battery && battery < 20) {
                    color = "yellow"
                }
                return <span>{battery && <ProgressBar value={battery} color={color} />}</span>
            }
        },
        {
            key: "vin",
            field: "vehicleInformation.vin",
            title: messages['devices_main_vin'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.vehicleInformation?.vin',
            code: "devices_main_vin",
            render: (item) => {
                return <span>{item?.vehicleInformation?.vin}</span>
            }
        },
        {
            key: "lastReading_eventName",
            field: "lastReading.eventName",
            title: messages['devices_main_last_event'],
            width: 250,
            show: true,
            locked: false,
            resize: true,
            export: true,
            sort: true, //VERIFY ADDING ON GQLNAMES on API to solve custom exception
            functionString: 'return row?.lastReading?.eventName || ""',
            code: "devices_main_last_event",
            render: (item) => {
                return <span>{item?.lastReading?.eventName || ""}</span>
            }
        },
        {
            key: "tags",
            field: "tags",
            title: messages['accounts_main_tags'],
            width: 350,
            show: true,
            locked: false,
            resize: true,
            code: "accounts_main_tags",
            render: (item) => {
                let unitTags = [];
                let items = item?.tags ? JSON.parse(item?.tags) : [];
                if (items) {
                    unitTags = items?.map(t => {
                        return { ...t, color: getTagColor(t?.color) };
                    });
                }

                unitTags = unitTags?.filter(unitTag => unitTag?.id != -1);

                return (
                    <TagsGroup
                        options={unitTags}
                        nameField="label"
                        colorField="color"
                    />
                )
            },
            export: true,
            functionString: 'return row?.tags?.map(x => x.label).join(",\r\n")'
        },
        {
            key: "unitConfig_billingAccount_name",
            field: "unitConfig.billingAccount { id name }", //simCard.simProviderAccount { name simProvider { name } }
            title: messages['devices_main_billing_account'],
            width: 350,
            show: true,
            locked: false,
            resize: true,
            sort: true,
            export: true,
            functionString: 'return row?.unitConfig?.billingAccount?.name || ""',
            code: "devices_main_billing_account",
            render: (item) => {
                return <span>{item?.unitConfig?.billingAccount?.name || ""}</span>
            }
        },
/*         {
            key: "maintenanceWarnings",
            field: "maintenanceWarnings",
            title: messages['devices_main_maintenance_warnings'] || 'Maintenance Warnings',
            width: 200,
            show: false,
            locked: false,
            resize: true,
            sort: true,
            export: true,
            functionString: 'return row?.maintenanceWarnings || ""',
            code: "devices_main_maintenance_warnings",
            render: (item) => {
                return <span>{item?.maintenanceWarnings || ""}</span>
            }
        } */
    ]);
    const [openPanel, setOpenPanel] = useState(false);
    const [openPanelDetails, setOpenPanelDetails] = useState(false);
    const [manufacturer, setManufacturer] = useState(null);
    const [deviceModel, setDeviceModel] = useState(null);
    const [brand, setBrand] = useState(null);
    const [simProviderAccount, setSimProviderAccount] = useState(null);
    const [networkType, setNetworkType] = useState(null);
    const [status, setStatus] = useState(null);
    const [total, setTotal] = useState(0);
    const [selectedItems, setSelectedItems] = useState([]);
    const [massSMS, setMassSMS] = useState(false);
    const [searchType, setSearchType] = useState("");
    const [installedOnly, setInstalledOnly] = useState(false);
    const [maintenanceWarnings, setMaintenanceWarnings] = useState(false);
    const [selectedTags, setSelectedTags] = useState([])
    const [hardwareVersion, setHardwareVersion] = useState(null);

    const [currentSort, setCurrentSort] = useState([{ field: "id", order: "ASC" }]); // Keep for defaultsConfig
    const LIMIT = 50; // Keep for defaultsConfig

    const SmartWitnessValidation = (items, title) => {
        if (items && items.length > 0) {
            let validateSmartWitness = selectedItems.some((item) => item.deviceModel.manufacturer.name?.toUpperCase() === "SmartWitness".toUpperCase());
            if (validateSmartWitness) {
                NotificationManager.warning(
                    messages['devices_main_smartWitness_error'],
                    title,
                    5000,
                );
                return true
            } else {
                return false
            }
        }
        return false
    }

    useEffect(() => {
        if (refreshResult) {
            dispatch(clearRedux("DEVICE_OVERVIEW", { refreshResult: null }))
            if (refreshResult.result) {
                NotificationManager.success(
                    messages['assign_firmware_firmware_cameras_updated'],
                    messages['assign_firmware_title'],
                    5000,
                );
            }
        }
    }, [refreshResult]);

    useEffect(() => {
        (!firmwares || firmwares.length === 0) && dispatch(searchEntityRequest({ key: "firmwares" }));
        (!events || events.length === 0) && dispatch(searchEntityRequest({ key: "events" }));
    }, []);

    // COMMENTED OUT: Cleanup effect - commonISRedux handles cleanup via clearOnUnmount prop
    // useEffect(() => {
    //     return () => {
    //         if (fetchPromiseRef.current) {
    //             fetchPromiseRef.current.abort();
    //         }
    //     };
    // }, []);

    // ============================================
    // DataViewInfiniteScroll Props Definitions
    // (Extracted for readability)
    // ============================================

    // onChangeConditions handler
    const handleChangeConditions = (tags, conditions, total, newColumns, rows, sort, key, fromPreferences, restoreOnly) => {
        // restoreOnly: data already persisted in redux, just sync UI states — skip getData
        if (restoreOnly) return;

        // Search tags cleared: if advanced filter conditions still exist, re-search with them only
        if (key === 'clearGrid') {
            const advancedConditions = (tableRef?.current?.conditions || []).filter(item => !item.tags);
            if (advancedConditions.length > 0) {
                tableRef?.current?.getData({ newConditions: advancedConditions });
            }
            return;
        }

        let currentConditions = {}
        let currentTags = {}
        let currentSearchType = "";
        if (!conditions && tags) {
            currentConditions = tableRef?.current?.conditions;
            currentTags = tags;
            currentSearchType = searchType;
        } else {
            currentConditions = conditions || [];

            let tagConditions = currentConditions?.filter((x) => x?.tags);
            currentTags = tagConditions[0]?.tags || [];
            if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);
            if (tagConditions[0]?.fields?.length === 1) {
                try {
                    let searchType = getDevicesTypeSearch(messages).find(x => x.id === tagConditions[0]?.fields[0])
                    if (searchType) {
                        setSearchType(searchType.id)
                        currentSearchType = searchType.id
                    }
                } catch (error) { }
            }
            //advanced filters
            //loading advanced filters by default
            let validateManufacturer = currentConditions && currentConditions?.find((x) => x?.fields && x?.fields[0] === "deviceModel_manufacturer_id");
            validateManufacturer && setManufacturer(validateManufacturer.terms[0]);

            let validatehardwareVersion = currentConditions && currentConditions?.find((x) => x?.fields && x?.fields[0] === "unitConfig_hardwareVersion");
            validatehardwareVersion && setHardwareVersion(validatehardwareVersion.terms[0]);

            let validateDeviceModel = currentConditions && currentConditions?.find((x) => x?.fields && x?.fields[0] === "deviceModel_id");
            validateDeviceModel && setDeviceModel(validateDeviceModel.terms[0]);

            let validateSimProviderAccount = currentConditions && currentConditions?.find((x) => x?.fields && x?.fields[0] === "simCard_simProviderAccount_id");
            validateSimProviderAccount && setSimProviderAccount(validateSimProviderAccount.terms[0]);

            let validateNetworkType = currentConditions && currentConditions?.find((x) => x?.fields && x?.fields[0] === "network");
            validateNetworkType && setNetworkType(validateNetworkType.terms[0]);

            let validateStatus = currentConditions && currentConditions?.find((x) => x?.fields && x?.fields[0] === "status");
            validateStatus && setStatus(validateStatus.terms[0]);

            let validateDealerId = currentConditions && currentConditions?.find((x) => x?.fields && x?.fields[0] === "company_dealerId");
            validateDealerId && setBrand(validateDealerId.terms[0]);

            let validateInstalledOnly = currentConditions && currentConditions?.find((x) => x?.fields && x?.fields[0] === "installed");
            validateInstalledOnly && setInstalledOnly(validateInstalledOnly.terms[0]);

            let validateMaintenanceWarnings = currentConditions && currentConditions?.find((x) => x?.range && x?.range.fields && x?.range.fields[0] === "maintenanceWarnings");
            validateMaintenanceWarnings && setMaintenanceWarnings(true)

            let validateTags = currentConditions && currentConditions?.find((tag) => tag?.fields && tag?.fields[0] === "tagIds");
            validateTags && setSelectedTags(validateTags?.terms);
        }
        let validateWarnings = currentConditions.find((item) => item?.range?.GTENumber)

        currentConditions = currentConditions?.filter((item) => item?.fields && item?.fields[0] !== 'id' && item?.fields[0] !== 'label' && !getDevicesTypeSearch(messages).find(x => x.id === item?.fields[0]));
        let tagsById = currentTags.filter((item) => typeof item.id === "number");
        if (tagsById?.length) {
            let currentIds = tagsById.map((item) => (item.id));
            if (currentIds.length > 0) {
                currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
            }
        }

        if (validateWarnings && validateWarnings?.range) {
            currentConditions.push({ range: { fields: ["maintenanceWarnings"], GTENumber: "1" } });
        }

        let tagsByLabel = currentTags.filter(item => typeof item.id === "string")

        let currentLabels = tagsByLabel.map((item) => item.label)
        if (currentLabels.length > 0) {
            currentConditions.unshift({ fields: currentSearchType === "" ? ["label", "esn", "imei", "company_name", "simCard_iccId", "reference", "vehicleInformation_vin", "simCard_msisdn", "unitConfig_billingAccount_name"] : [currentSearchType], terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
        }
        // Handle search/tag changes - getData called regardless of add/remove action
        // (Follows DevicesTab pattern for consistency)
        if (!conditions && tags && !deviceDetailsOpenModal) {
            tableRef?.current?.getData({ newSort: sort, newConditions: currentConditions, newColumns });
            if (!currentConditions.length) {
                setTotal(0)
                resetWarnings()
                dispatch(clearRedux("COMMON", { "units": {} }))
                tableRef?.current?.conditionsHandler(currentConditions)
            }
        } else {
            if (conditions && conditions.length > 0) {
                tags = conditions.filter(x => x.tags);
            }
            if (!deviceDetailsOpenModal && tags && tags.length > 0) {
                tableRef?.current?.getData({ newSort: sort, newConditions: currentConditions, newColumns });
            }
            if (!currentConditions.length) {
                setTotal(0)
                resetWarnings()
                dispatch(clearRedux("COMMON", { "units": {} }))
                tableRef?.current?.conditionsHandler(currentConditions)
            }
        }
    };

    // onChangeColumns handler
    const handleChangeColumns = (items) => {
        let currentColumns = items.map((item) => ({
            ...item,
            title: item.code ? messages[item.code] : item.title,
            render: item.key === "status" ? (item) => {
                let nextScheduledAction = item?.nextScheduledAction
                let message = null
                if (nextScheduledAction) {
                    try {
                        let data = JSON.parse(nextScheduledAction)
                        if (data && data?.action === "statusUpdate") {
                            message = `${messages['accounts_main_next_scheduled_action']}: ${data?.scheduleOn}`
                            let status = statusOptions.find(item => item?.id === data?.data?.newStatusId)
                            message = `${messages[data?.action] || data?.action}${status ? ` ${messages[status?.label] || status?.label}` : ''} ${messages['accounts_main_at']} ${data?.scheduleOn}`
                        }
                    } catch (exc) { }
                }
                return (
                    <div style={{ display: 'flex' }}>
                        <ChipStatus statusCode={item?.status} messages={messages} />
                        {nextScheduledAction &&
                            <div style={{ marginLeft: 5 }}>
                                <Tooltip title={message}>
                                    <Icon style={{ color: '#1664f5', cursor: 'pointer' }}>access_alarms</Icon>
                                </Tooltip>
                            </div>
                        }
                    </div>
                )
            } : item.key === "linkedMode" ? (item) => {
                return (
                    <span>
                        {
                            (item?.deviceModel?.manufacturer?.name?.toUpperCase() === "Vidfleet".toUpperCase() || item?.deviceModel?.manufacturer?.name?.toUpperCase() === "Smartwitness".toUpperCase() || item?.deviceModel?.manufacturer?.name?.toUpperCase() === "LightMetrics".toUpperCase()) && getBadget(item?.linkedMode ? messages["devices_import_camera_paired_mode"] : messages["devices_import_camera_gps"], item?.linkedMode)
                        }
                    </span>
                )
            } : item.key === "installed" ? (item) => {
                return <Chip style={{ backgroundColor: item?.installed ? "#66BB6A" : "#5A5A5A", color: "#FFFFFF" }} label={item?.installed ? messages['devices_main_installed'] : messages['devices_main_pending_installed'] || ''}></Chip>
            } : item.render,
        }))
        if (!deviceDetailsOpenModal) {
            setColumns([...currentColumns])
        }
    };

    // extraFilters render function
    const renderExtraFilters = () => {
        return (
            <div className="extra-filters-tab" style={{ justifyContent: 'end', display: 'flex', alignItems: 'center' }}>
                <Select
                    id="search-type-id"
                    valueField="id"
                    textField="label"
                    value={getDevicesTypeSearch(messages).find(x => x.id === searchType)}
                    placeholder={messages['account_change_status_label']}
                    options={getDevicesTypeSearch(messages) || []}
                    onChange={(searchType) => {
                        setSearchType(searchType ? searchType.id : "");
                        let currentConditions = tableRef?.current?.conditions;
                        let newConditions = currentConditions.filter(x => !x.tags)
                        let tagsConditions = currentConditions.filter(x => x.tags);

                        if (tagsConditions?.length > 0) {
                            let openTags = tagsConditions[0].tags.filter(item => !item.company)
                            let closetags = tagsConditions[0].tags.filter(item => item.company)

                            if (openTags?.length > 0) {
                                let currentLabels = openTags.map((item) => item.label)
                                if (currentLabels.length > 0) {
                                    newConditions.push({ fields: searchType.id === "" ? ["label", "esn", "imei", "company_name", "simCard_iccId", "reference", "vehicleInformation_vin", "simCard_msisdn"] : [searchType.id], terms: currentLabels, exact: false, tags: tagsConditions[0].tags, conditionOperator: "OR" })
                                }
                            }

                            if (closetags?.length > 0) {
                                let currentIds = closetags.map((item) => item.id)
                                if (currentIds.length > 0) {
                                    newConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: tagsConditions[0].tags, conditionOperator: "OR" });
                                }
                            }
                        }
                    }}
                />
            </div>
        )
    };

    // customColumn definition — memoized so DataViewInfiniteScroll only re-renders when
    // warnings data or language actually changes, not on every parent render
    const customColumnConfig = useMemo(() => ({
        render: (row) => {
            const warningData = warningsMap[row.id];
            if (!warningData) return null;

            const isOffline = warningData.isOffline !== null ? warningData.isOffline : false;
            const warnings = warningData.maintenanceWarnings || 0;
            let isSdCardError = false;
            try {
                const sdCard = warningData.sdCard ? JSON.parse(warningData.sdCard) : null;
                const sdCardStatus = sdCard?.status?.toUpperCase();
                isSdCardError = sdCardStatus && sdCardStatus !== "NORMAL";
            } catch (exc) { }

            const { title, color } = getWarningCategory(isSdCardError, warnings, isOffline, messages);
            if (!title) return null;

            return (
                <div>
                    <Tooltip title={title}>
                        <Icon style={{ cursor: "pointer", color: color }} className="toolbar-list-icon fs-20" onClick={() => {
                            if (isSdCardError || warnings) {
                                dispatch(openModalMaintenanceWarnings({ ...row, ...warningData }))
                            }
                        }} >
                            warning
                        </Icon>
                    </Tooltip>
                </div>
            );
        }
    }), [warningsMap, messages]);

    // options array for DataViewInfiniteScroll
    const tableOptions = [
        {
            id: "edit-device",
            label: messages['devices_main_edit'],
            show: true,
            single: true,
            disabled: !HasPermission(user, "unt", "u"),
            action: () => {
                let validation = false;//SmartWitnessValidation(selectedItems, messages['devices_main_edit'])
                if (!validation) {
                    if (selectedItems[0]?.refurbishedUnit?.refurbishedUnitId) {
                        NotificationManager.warning(
                            messages['device_overview_refurbish_message'],
                            messages['device_overview_title'],
                            3000,
                            null,
                            null,
                            'error'
                        );
                    } else
                        dispatch(featureTabOpenModal(selectedItems[0].id))
                }
            },
        },
        {
            id: "edit-and-swip-card",
            label: messages['devices_main_edit_swap_sim_card'],
            show: true,
            single: true,
            disabled: !HasPermission(user, "untessc", "u"),
            action: () => {
                let validation = false;//SmartWitnessValidation(selectedItems, messages['devices_main_edit_swap_sim_card'])
                if (!validation) {
                    dispatch(editSwapSimCardOpenModal(selectedItems))
                }
            }
        },
        {
            id: "bulk-features",
            label: messages['devices_main_bulk_features'],
            show: true,
            disabled: !HasPermission(user, "untba", "u"),
            multiple: true,
            action: () => {
                if (selectedItems.length > 0) {
                    let manufacturer = selectedItems[0].deviceModel?.manufacturer?.name;
                    let deviceModel = selectedItems[0].deviceModel?.name;
                    let validation = false //SmartWitnessValidation(selectedItems, messages['devices_main_bulk_features'])
                    if (!validation) {
                        let validationMD = selectedItems.some((item) => (item.deviceModel.manufacturer.name !== manufacturer || item.deviceModel.name !== deviceModel));
                        let rma = selectedItems.some((item) => item.status === 6)
                        if (validationMD) {
                            NotificationManager.warning(
                                messages['devices_main_same_manufacturer_device_model'],
                                messages['devices_main_bulk_features'],
                                5000
                            );
                        } else if (rma) {
                            NotificationManager.warning(
                                messages['devices_tab_rma_selected'],
                                messages['devices_main_bulk_features'],
                                5000
                            );
                        } else {
                            dispatch(bulkActionsOpenModal({ devices: selectedItems, bulkFeatures: true }))
                        }
                    }
                } else {
                    NotificationManager.warning(
                        messages['devices_main_select_devices'],
                        messages['devices_main_bulk_features'],
                        5000
                    );
                }
            }
        },
        {
            id: "bulk-tags",
            label: messages['devices_main_bulk_tags'],
            show: true,
            disabled: !HasPermission(user, "assgntgs", "c"),
            multiple: true,
            action: () => {
                if (selectedItems.length > 0) {
                    dispatch(bulkTagsOpenModal(selectedItems))
                } else {
                    NotificationManager.warning(
                        messages['devices_main_select_devices'],
                        messages['devices_main_bulk_tags'],
                        5000
                    );
                }
            }
        },
        {
            id: "devices_tab_assign_unassign_billing_account",
            label: messages['devices_tab_assign_unassign_billing_account'],
            show: true,
            disabled: !HasPermission(user, "assngbllngaccnts", "c"),
            multiple: true,
            action: () => {
                const areSameBrand = selectedItems.every(item => item?.company?.dealer?.id === selectedItems[0]?.company?.dealer?.id);
                if (areSameBrand) {
                    dispatch(openModalAssignBillingAccount({ selectedDevices: selectedItems }))
                } else {
                    NotificationManager.warning(
                        messages['devices_main_different_brands'],
                        messages['devices_main_assign_unassign_billing_account'],
                        5000
                    );
                }
            }
        },
        {
            id: "create-diagnostic-device",
            label: messages['devices_main_create_diagnostic_device'],
            show: true,
            disabled: !HasPermission(user, "dvdiag", "c"),
            action: () => {
                dispatch(createDiagnosticDeviceOpenModal(selectedItems))
            }
        },
        {
            id: "camera-actions",
            label: messages['devices_main_camera_actions'],
            show: true,
            disabled: !HasPermission(user, "cact"),
            submenu: [
                {
                    id: "bulk-reboot-devices",
                    label: messages['reboot_device_bulk_reboot'],
                    show: true,
                    disabled: false,
                    action: () => {
                        if (selectedItems.length > 0) {
                            let validateSelectedDevices = selectedItems.some((item) => item?.deviceModel?.manufacturer?.name !== "VidFleet");
                            if (validateSelectedDevices) {
                                NotificationManager.warning(
                                    messages["devices_main_only_vidfleet"],
                                    messages['reboot_device_bulk_reboot'],
                                    5000
                                );
                            } else {
                                dispatch(rebootDeviceOpenModal(selectedItems))
                            }
                        } else {
                            dispatch(rebootDeviceOpenModal([]))
                        }
                    }
                },
                {
                    id: "bulk-pairing",
                    label: messages['devices_main_bulk_pairing'],
                    show: true,
                    disabled: !HasPermission(user, "untbp", "u"),
                    action: () => {
                        dispatch(bulkPairingOpenModal())
                    }
                },
                {
                    id: "bulk-vidfleet-configuration",
                    label: messages['devices_main_bulk_vidfleet_configuration'],
                    show: true,
                    disabled: !HasPermission(user, "vftcd", "u"),
                    action: () => {
                        if (selectedItems.length > 0) {
                            let first = selectedItems[0];
                            let validateSelectedDevicesV = selectedItems.some((item) => item?.deviceModel.manufacturer?.name !== "VidFleet");
                            let validateDeviceModel = selectedItems.some((item) => item?.deviceModel?.name !== first.deviceModel.name);

                            if (!validateSelectedDevicesV && !validateDeviceModel) {
                                dispatch(vidfleetConfigurationBulkOpenModal());
                            } else {
                                if (validateDeviceModel) {
                                    NotificationManager.warning(
                                        messages["devices_main_bulk_vidfleet_config_device_model"],
                                        messages['devices_main_bulk_vidfleet_configuration'],
                                        5000
                                    );
                                } else {
                                    NotificationManager.warning(
                                        messages["devices_main_only_vidfleet"],
                                        messages['devices_main_bulk_vidfleet_configuration'],
                                        5000
                                    );
                                }
                            }
                        } else {
                            dispatch(vidfleetConfigurationBulkOpenModal());
                        }
                    }
                },
                {
                    id: "bulk-vidfleet-configuration-upload-mode",
                    label: messages['devices_main_bulk_vidfleet_configuration_upload_mode'],
                    show: true,
                    disabled: !HasPermission(user, "vftcdum", "u"),
                    action: () => {
                        if (selectedItems.length > 0) {
                            let validateSelectedDevicesV = selectedItems.some((item) => item?.deviceModel.manufacturer?.name !== "VidFleet");
                            if (!validateSelectedDevicesV) {
                                dispatch(vidfleetConfigurationUploadModeLoadData());
                            } else {
                                NotificationManager.warning(
                                    messages["devices_main_only_vidfleet"],
                                    messages['devices_main_bulk_vidfleet_configuration_upload_mode'],
                                    5000
                                );
                            }
                        } else {
                            dispatch(vidfleetConfigurationUploadModeLoadData());
                        }
                    }
                },
                {
                    id: "devices_tab_bulk_vidfleet_config_hubspot",
                    label: messages['devices_tab_bulk_vidfleet_config_hubspot'],
                    show: true,
                    disabled: !HasPermission(user, "vftunat", "u"),
                    action: () => {
                        if (selectedItems.length > 0) {
                            let validateSelectedDevicesV = selectedItems.some((item) => item?.deviceModel.manufacturer?.name !== "VidFleet");
                            let validateSimProvider = selectedItems.some((item) => item?.simCard?.simProviderAccount?.simProvider?.name !== "AT&T")
                            if (!validateSelectedDevicesV) {
                                if (!validateSimProvider)
                                    dispatch(vidfleetConfigurationHubSpotLoadData());
                                else {
                                    NotificationManager.warning(
                                        messages["devices_main_only_att"],
                                        messages['devices_tab_bulk_vidfleet_config_hubspot'],
                                        5000
                                    );
                                }
                            } else {
                                NotificationManager.warning(
                                    messages["devices_main_only_vidfleet"],
                                    messages['devices_tab_bulk_vidfleet_config_hubspot'],
                                    5000
                                );
                            }
                        } else {
                            dispatch(vidfleetConfigurationHubSpotLoadData());
                        }
                    }
                },
                {
                    id: "assign-firmware",
                    label: messages['devices_main_assign_firmware'],
                    show: true,
                    disabled: !HasPermission(user, "untaf", "u"),
                    action: () => {
                        let currentSelectedItems = selectedItems;
                        let validateVidfleet = currentSelectedItems.some((item) => item.deviceModel.manufacturer?.name !== "VidFleet");
                        if (!validateVidfleet) {
                            dispatch(assignFirmwareOpenModal(currentSelectedItems))
                        } else {
                            NotificationManager.warning(
                                messages['devices_main_only_vidfleet'],
                                messages['devices_main_assign_firmware'],
                                4000,
                            );
                        }
                    }
                },
                {
                    id: "activate-sim-card",
                    label: messages['devices_main_activate_sim_card'],
                    multiple: true,
                    show: true,
                    disabled: !HasPermission(user, "untasc", "u"),
                    action: () => {
                        let currentSelectedItems = selectedItems
                        let validateVidfleet = currentSelectedItems.some((item) => item.deviceModel.manufacturer?.name !== "VidFleet")
                        if (validateVidfleet) {
                            NotificationManager.warning(
                                messages['devices_main_activate_sim_card_message_error'],
                                messages['devices_main_activate_sim_card'],
                                3000
                            );
                            return
                        }
                        dispatch(activateCameraSimCardOpenModal(currentSelectedItems))
                    }
                },
                {
                    id: "unpairing-device",
                    label: messages['devices_bulk_unpairing_unpair'],
                    show: true,
                    disabled: !HasPermission(user, "untbu", "u"),
                    action: () => {
                        if (selectedItems.length > 0) {
                            let validateVidfleet = selectedItems.some((item) => item.deviceModel.manufacturer.name.toUpperCase() !== "VIDFLEET" && item.deviceModel.manufacturer.name.toUpperCase() !== "SMARTWITNESS");
                            if (!validateVidfleet) {
                                let validateLinkedUnitId = selectedItems.some((item) => item.linkedUnitId === null);
                                if (!validateLinkedUnitId) {
                                    dispatch(bulkUnpairingOpenModal(selectedItems));
                                } else {
                                    NotificationManager.warning(
                                        messages['devices_bulk_unpairing_devices_mush_have_other_linked_devices'],
                                        messages['devices_bulk_unpairing_unpair'],
                                        5000,
                                    );
                                }
                            } else {
                                NotificationManager.warning(
                                    messages['devices_main_only_vidfleet_and_smartwitness'],
                                    messages['devices_bulk_unpairing_unpair'],
                                    5000,
                                );
                            }
                        } else dispatch(bulkUnpairingOpenModal([]));
                    }
                },
            ]
        },
        {
            id: "change-actions",
            label: messages['devices_main_change'],
            show: true,
            disabled: !HasPermission(user, "chact"),
            submenu: [
                {
                    id: "change_status",
                    label: messages['devices_tab_change_status'],
                    show: true,
                    disabled: !HasPermission(user, "untcsu", "u"),
                    multiple: true,
                    action: () => {
                        console.log({selectedItems})
                        
                        let validation = !HasPermission(user, "chngststsactv", "u") ? selectedItems.some((item) => item.status === 1) : false;
                        let refurbishedValidation = selectedItems.some((item) => item.refurbishedUnit?.refurbishedUnitId)
                        if (refurbishedValidation) {
                            NotificationManager.warning(
                                messages['devices_tab_change_status_warning'],
                                messages['devices_tab_change_status'],
                                5000,
                            );
                            return
                        }
                        if (!validation) {
                            dispatch(changeStatusOpenModal(selectedItems))
                        } else {
                            NotificationManager.warning(
                                messages['devices_tab_change_status_active_message'],
                                messages['devices_tab_change_status'],
                                5000,
                            );
                        }
                    }
                },
                {
                    id: "change-sim-provider",
                    label: messages['change_sim_provider_title'],
                    show: true,
                    disabled: !HasPermission(user, "untcspa", "u"),
                    action: () => {
                        if (selectedItems.length > 0) {
                            let validateGlobalStart = selectedItems.some((item) => item.deviceModel.manufacturer.name !== "Global Star");
                            if (validateGlobalStart) {
                                let validation = false //SmartWitnessValidation(selectedItems, messages['change_sim_provider_title'])
                                if (!validation) dispatch(changeSimProviderOpenModal(selectedItems))

                            } else {
                                NotificationManager.warning(
                                    messages['devices_main_global_star_error'],
                                    messages['change_sim_provider_title'],
                                    5000,
                                );
                            }
                        } else {
                            dispatch(changeSimProviderOpenModal([]))
                        }
                    }
                },
                {
                    id: "change-puls-customer",
                    label: messages['change_puls_customer_title'],
                    show: true,
                    disabled: !HasPermission(user, "untcpc", "u"),
                    action: () => {
                        if (selectedItems.length > 0) {
                            let currentCalampApi = selectedItems[0].unitConfig?.calampApi
                            let calampApiValidation = selectedItems.some((item) => item?.unitConfig?.calampApi !== currentCalampApi)
                            if (calampApiValidation) {
                                NotificationManager.warning(
                                    messages['devices_main_warning_message_calamp_api'],
                                    messages['change_puls_customer_title'],
                                    5000,
                                );
                            } else {
                                let validation = false //SmartWitnessValidation(selectedItems, messages['change_puls_customer_title'])
                                if (!validation) dispatch(changePulsCustomerOpenModal(selectedItems))
                            }
                        } else {
                            dispatch(changePulsCustomerOpenModal([]))
                        }
                    }
                },
                {
                    id: "change-script",
                    label: messages['change_script_title'],
                    show: true,
                    disabled: !HasPermission(user, "untcs", "u"),
                    action: () => {
                        if (selectedItems.length > 0) {
                            let manufacturer = selectedItems[0].deviceModel.manufacturer?.name;
                            let validation = selectedItems.some((item) => item.deviceModel.manufacturer.name !== manufacturer);
                            if (validation) {
                                NotificationManager.warning(
                                    messages['devices_tab_change_script_validation'],
                                    messages['devices_tab_change_script'],
                                    5000,
                                );
                            } else {
                                dispatch(changeScriptOpenModal(selectedItems))
                            }
                        } else {
                            dispatch(changeScriptOpenModal([]))
                        }
                    }
                },
                {
                    id: "change-timezone",
                    label: messages['devices_tab_change_timezone'],
                    show: true,
                    disabled: !HasPermission(user, "untctz", "u"),
                    action: () => {
                        let validation = false;//SmartWitnessValidation(selectedItems, messages['devices_tab_change_timezone']);
                        let refurbishedValidation = selectedItems.some((item) => item.refurbishedUnit?.refurbishedUnitId)
                        if (refurbishedValidation) {
                            NotificationManager.warning(
                                messages['devices_tab_change_timezone_warning'],
                                messages['devices_tab_change_timezone'],
                                5000,
                            );
                            return
                        }
                        if (!validation) dispatch(changeTimezoneOpenModal(selectedItems))
                    }
                },
            ]
        },
        {
            id: "Commands-actions",
            label: messages['devices_main_commands'],
            show: true,
            disabled: !HasPermission(user, "coact"),
            submenu: [
                {
                    id: "send-command",
                    label: messages['devices_main_send_command'],
                    show: true,
                    disabled: !HasPermission(user, "sndcom", "u"),
                    action: () => {
                        let validation = SmartWitnessValidation(selectedItems, messages['devices_main_edit_swap_sim_card'])
                        if (!validation) {
                            if (selectedItems.length > 0) {
                                let manufacturer = selectedItems[0].deviceModel.manufacturer?.name.toUpperCase();
                                let validateVidfleet = selectedItems.some((item) => item.deviceModel.manufacturer?.name.toUpperCase() !== "VidFleet".toUpperCase());
                                if (validateVidfleet) {
                                    let manufacturerValidation = selectedItems.some((item) => item.deviceModel.manufacturer.name.toUpperCase() !== manufacturer);
                                    if (manufacturerValidation) {
                                        NotificationManager.warning(
                                            messages['devices_main_the_manufacturer_of_the_devices_must_be_the_same'],
                                            messages['devices_main_send_command'],
                                            5000
                                        );
                                    } else {
                                        dispatch(sendCommandOpenModal({ selectedDevices: selectedItems, massSMS: false }))
                                    }
                                } else {
                                    NotificationManager.warning(
                                        messages['devices_main_the_manufacturer_of_the_devices_must_be_different_from_vidfleet'],
                                        messages['devices_main_send_command'],
                                        5000
                                    );
                                }
                            } else dispatch(sendCommandOpenModal());
                        }
                    }
                }
            ]
        },
        {
            id: "devices_main_mark_as_rma",
            label: messages['devices_main_mark_as_rma'],
            show: true,
            disabled: !HasPermission(user, "untrma", "u"),
            action: () => {
                let validation = false //SmartWitnessValidation(selectedItems, messages['devices_main_edit_swap_sim_card'])
                if (!validation) {
                    let validateRMA = selectedItems.some((item) => item.status === 6);
                    let validateRefurbish = selectedItems.some((item) => item?.refurbishedUnit?.refurbishedUnitId);
                    if (validateRefurbish) {
                        NotificationManager.warning(
                            messages['mark_as_rma_devices_is_already_refirbished'],
                            messages['mark_as_rma_title'],
                            4000,
                        );
                    } else if (validateRMA) {
                        NotificationManager.warning(
                            messages['mark_as_rma_devices_is_already_rma'],
                            messages['mark_as_rma_title'],
                            4000,
                        );
                    } else dispatch(markAsRmaOpenModal(selectedItems));
                }
            }
        },
                            {
                                id: "cast-import-manual",
                                label: messages['devices_main_cast_import_manual'],
                                show: true,
                                disabled: !HasPermission(user, "unt"),
                                action: () => {
                                    dispatch(openModalCastImportManual());
                                }
                            },
    ];

    let innerHeight = window.innerHeight

    return (
        <Container maxWidth={false} className="container-view">
            <Paper elevation={0}>
                <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    style={{ padding: "14px 18px", borderBottom: "1px solid #e8e8e8", height: '95v' }}
                >
                    <Grid item xs={3}>
                        <Typography variant="h5" gutterBottom component="div">
                            {messages['devices_main_devices']} {/* ({total}) */}
                        </Typography>
                    </Grid>
                    <Grid item xs={9} className="main-view-buttons" style={{ display: 'flex', alignItems: 'center' }}>
                        <NavLink to={`/app/transactions/list`} style={{ color: 'black', marginRight: 5 }}>
                            <span style={{ fontSize: 14 }}>{messages['devices_main_import_device_result']}</span>
                        </NavLink>
                        <Button
                            className="btn-dark-blue"
                            onClick={() => {
                                dispatch(bulkActionsOpenModal())
                            }}
                            disabled={!HasPermission(user, "untba", "u")}
                        >
                            {messages['devices_main_bulk_actions']}
                        </Button>
                        <Button
                            className="btn-dark-blue"
                            onClick={() => {
                                dispatch(devicesImportShow());
                            }}
                            disabled={!HasPermission(user, "impdv", "c")}
                        >
                            {messages['devices_main_import_and_assign']}
                        </Button>
                    </Grid>

                    <div style={{ width: '100%', marginBottom: 10, marginTop: 5 }}>
                        <Divider style={{ backgroundColor: '#999999' }} />
                    </div>

                    <DataViewInfiniteScroll
                        key="pm_main_devices_list"
                        ref={tableRef}
                        clearOnUnmount={false}
                        
                        // /* Core props (not deprecated, remain flat) */
                        name="pm-devices-v4"
                        columns={columns}
                        infiniteScroll={false}
                        entity="devices"
                        graphqlConfig={{
                            function: "searchUnits",           
                            sortType: "UnitSortInput",         
                            conditionsType: "UnitSearchInput", 
                            dynamicQuery: true,      
                            totalBaseField: "esn",           
                            customQuery: "id esn nextScheduledAction companyId deviceModel { id name manufacturer { id name } } linkedUnitId unitConfig { hardwareVersion firmwareId, calampApi } refurbishedUnit { refurbishedUnitId } timezone simCard { simProviderAccount { name simProvider { name } } iccId } linkedMode linkedByUnit { id label deviceModelId }",
                        }}

                        searchConfig={{
                            placeholder: messages['devices_main_search_placeholder'],
                            conditions: ["label", "esn", "imei", "company_name", "simCard_msisdn"],
                            fields: ["label", "esn", "imei", "company_name", "simCard_iccId", "reference", "vehicleInformation_vin", "simCard_msisdn"], 
                            optionValue: "id",   
                            optionLabel: "label",
                            extraField: "company { id name }",
                            onlyEnter: true,
                            clearGridOnEmpty: true,
                            tagsLimit: 200
                        }}

                        exportConfig={{
                            fileName: "devices",
                            limit: 50
                        }}
                        
                        selectionConfig={{ showCounter: false }}

                        uiConfig={{
                            topHeight: 200,
                            hideSplit: false,
                            hideAllInPagination: true,
                        }}

                        defaultsConfig={{
                            sort: currentSort, 
                            rowsPerPage: LIMIT,
                        }}

                        onFilters={() => { setOpenPanel(!openPanel) }}
                        onChangeColumns={handleChangeColumns}
                        onChangeConditions={handleChangeConditions}
                        onSelectedItems={setSelectedItems}
                        
                        options={tableOptions}
                        extraFilters={renderExtraFilters}
                        customColumn={customColumnConfig}
                        tableButtons={["export", 'orderColumn']}
                    />
                </Grid>
            </Paper>
            {
                !openPanelDetails &&
                <AdvancedFilters
                    setOpenPanel={setOpenPanel}
                    openPanel={openPanel}
                    tableRef={tableRef}
                    manufacturer={manufacturer}
                    setManufacturer={setManufacturer}
                    hardwareVersion={hardwareVersion}
                    setHardwareVersion={setHardwareVersion}
                    deviceModel={deviceModel}
                    setDeviceModel={setDeviceModel}
                    simProviderAccount={simProviderAccount}
                    setSimProviderAccount={setSimProviderAccount}
                    networkType={networkType}
                    setNetworkType={setNetworkType}
                    status={status}
                    setStatus={setStatus}
                    brand={brand}
                    setBrand={setBrand}
                    setTotal={setTotal}
                    installedOnly={installedOnly}
                    setInstalledOnly={setInstalledOnly}
                    maintenanceWarnings={maintenanceWarnings}
                    setMaintenanceWarnings={setMaintenanceWarnings}
                    selectedTags={selectedTags}
                    setSelectedTags={setSelectedTags}
                    autoCloseOnApply
                />
            }
            {deviceDetailsOpenModal && <Details />}
            {openModalVidFleetConfiguration && <VidfleetConfiguration bulk devices={selectedItems} />}
            {configurationHubSpotOpenModal && <ConfigurationHubSpot initialDevices={selectedItems} mode={"Bulk"} />}
            {bulkAOpenModal && <BulkActions principalTableRef={tableRef} />}
            {activeSimCardOpenModal && <ActivateSimCard />}
            {changeSimPOpenModal && <ChangeSimProvider tableRef={tableRef} />}
            {bulkPOpenModal && <BulkPairing tableRef={tableRef} />}
            {configurationUploadModeOpenModal && <ConfigurationUploadMode initialDevices={selectedItems} mode={"Bulk"} />}
            {importDevicesOpenModal && <ImportDevices />}
            {bulkUnOpenModal && <BulkUnpairing tableRef={tableRef} />}
            {assignFirmOpenModal && <AssignFirmware />}
            {deviceInfoOpenModal && <VidfleetInformation />}
            {editSwapSimCardOModal && <EditSwapSimCard tableRef={tableRef} />}
            {editDeviceOpenModal && <UpdateFeature tableRef={tableRef} />}
            {sendCommandsOpenModal && !massSMS && <SendCommand massSMS={false} setMassSMS={setMassSMS} />}
            {cgPulsCustomerOpenModal && <ChangePulsCustomer tableRef={tableRef} />}
            {cgScriptOpenModal && <ChangeScript tableRef={tableRef} />}
            {openModalMarkAsRma && <MarkAsRma tableRef={tableRef} />}
            {changeStatusOModal && <ChangeStatus tableRef={tableRef} />}
            {changeTimezoneOModal && <ChangeTimezone tableRef={tableRef} />}
            {rebootDeOpenModal && <RebootDevice />}
            {openModalCoverageDetails && <CoverageDetails />}
            {openModalCreateDiagnosticDevice && <CreateDiagnosticDevice />}
            {openModalViewMaintenanceWarnings && <MaintenanceWarnings isTab={false} tableRef={tableRef} />}
            {
                /* Video Fleet */
                openModalVidFleetVideo &&
                <VidFleetOverview />
            }
            {openModalBulkTags && <BulkTags entityName={"Unit"} tableRef={tableRef} />}
            {openModalActivities && <Activities noFilters2 noOpenSearch2 modalView noUsers height={innerHeight - 257} />}
            {assignBillingAccountOpenModal && <AssignBillingAccount tableRef={tableRef} dealerId={selectedItems[0]?.company?.dealer?.id} />}
            {castImportManualOpenModal && <CastImportManual />}
        </Container>
    )
}
export default DevicesMain;